import { useQuery } from '@tanstack/react-query';
import IncidentReview from '../../views/IncidentReview';
import { useApiClients } from '../../client/api/useApiClient';
import { useSearchParams } from 'react-router';
import {
  Department,
  GetAllDepartmentResponse,
  GetAllIncidentTypesResponse,
  GetUserIncidentsResponseOrganization,
  IncidentsSeverityEnum,
  IncidentsStatusEnum,
  IncidentTypes,
} from '@EHSNavigator/backend/dist/api';
import Loader from '../../components/Loader';

export default function IncidentReviewPage() {
  const { IncidentReportingApi, DepartmentApi } =
    useApiClients();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') ?? '';
  const currentPage = searchParams.get('page') ?? 1;
  const status = searchParams.get('status') ?? null;
  const severity = searchParams.get('severity') ?? null;
  const department = searchParams.get('department') ?? '';
  const type = searchParams.get('type') ?? '';
  const limit = Number(searchParams.get('limit')) || 10;

  const query: any = {
    page: Number(currentPage),
  };
  if (searchQuery) query.search = searchQuery;
  if (department) query.departmentId = department;
  if (status) query.status = status as IncidentsStatusEnum;
  if (type) query.incidentTypeId = type;
  if (severity) query.severity = severity as IncidentsSeverityEnum;
  if (limit) query.limit = Number(limit);

  const {
    data: incidents,
    isLoading,
    isRefetching,
  } = useQuery<GetUserIncidentsResponseOrganization, Error, any>({
    queryKey: [
      'all-organization-incidents',
      searchQuery,
      Number(currentPage),
      status,
      severity,
      department,
      type,
      Number(limit),
    ],
    queryFn: async () => {
      const response =
        await IncidentReportingApi.incidentReportingControllerFindAllIncidents({
          search: searchQuery,
          page: Number(currentPage) || 1,
          limit: Number(limit),
          status: status as IncidentsStatusEnum,
          severity: severity as IncidentsSeverityEnum,
          departmentId: department,
          incidentTypeId: type,
        });

      return response?.data;
    },
  });

  const { data: allDepartments, isLoading: isDepartmentLoading } =
    useQuery<GetAllDepartmentResponse>({
      queryKey: ['departments'],
      queryFn: async () => {
        const response =
          await DepartmentApi.departmentControllerFindAllDepartment({
            disablePagination: true,
          });
        return response?.data;
      },
    });

  const { data: incidentTypesResponse, isLoading: isIncidentTypesLoading } =
    useQuery<GetAllIncidentTypesResponse>({
      queryKey: ['incident-types'],
      queryFn: async () => {
        const response =
          await IncidentReportingApi.incidentReportingControllerFindAllIncidentTypes(
            {
              removeIllness: true,
            },
          );
        return response?.data;
      },
    });

  const departments =
    allDepartments?.departments?.map((node: Department) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const incidentTypes =
    incidentTypesResponse?.incidentTypes?.map((node: IncidentTypes) => ({
      label: node?.name,
      value: node?.id,
    })) || [];

  const hasFilters =
    !!searchQuery || !!department || !!status || !!type || !!severity;

  const noDataMessage = hasFilters
    ? 'No items match your filters or search. Try using different keywords maybe.'
    : 'This looks empty. Looks like no incidents have been reported till now.';
  if (isDepartmentLoading || isIncidentTypesLoading) {
    return <Loader />;
  }

  return (
    <div className="flex w-full flex-1 flex-col px-15 pt-14 pb-10 md:px-25 lg:pr-14 lg:pb-20 lg:pl-8">
      <IncidentReview
        data={incidents as GetUserIncidentsResponseOrganization}
        departments={departments}
        incidentTypes={incidentTypes}
        noDataMessage={noDataMessage}
        params={{
          search: searchQuery,
          page: Number(currentPage),
          departmentId: department,
          status: status as IncidentsStatusEnum,
          severity: severity as IncidentsSeverityEnum,
          incidentTypeId: type,
          limit: Number(limit),
        }}
        isIncidentsLoading={isLoading || isRefetching}
        allDraftIncidentReviewsLength={
          incidents?.draftCorrectiveActionsCount > 0
            ? incidents?.draftCorrectiveActionsCount
            : undefined
        }
      />
    </div>
  );
}
